import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Api from '@plone/volto/helpers/Api/Api';
import {
  createContent,
  getContent,
} from '@plone/volto/actions/content/content';
import { searchContent } from '@plone/volto/actions/search/search';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import './ai-image-tool.scss';

type DataFile = {
  data: string;
  'content-type': string;
  filename: string;
  encoding: 'base64';
};

const MAX_INPUT_BYTES = 8 * 1024 * 1024;

const readFileAsBase64 = (file: File): Promise<DataFile> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const match = result.match(/^data:(.*);base64,(.*)$/);
      if (!match) {
        reject(new Error('Format de fichier non reconnu.'));
        return;
      }
      resolve({
        data: match[2],
        'content-type': match[1] || 'image/png',
        filename: file.name,
        encoding: 'base64',
      });
    };
    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    reader.readAsDataURL(file);
  });

const makeTitle = (text: string) => {
  const t = text.trim();
  if (!t) return 'Image IA';
  return t.length > 50 ? t.slice(0, 50) + '…' : t;
};

const AIImageTool = (props: any) => {
  const { pathname } = props;
  const dispatch = useDispatch();

  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('auto');
  const [quality, setQuality] = useState('auto');
  const [inputImage, setInputImage] = useState<DataFile | null>(null);
  const [previewB64, setPreviewB64] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apiRef = useRef<any>(null);
  if (!apiRef.current) apiRef.current = new Api();

  const content = useSelector((state: any) => state.content.data);
  const createdImage = useSelector(
    (state: any) => state.content.subrequests?.['ai-image-tool']?.data,
  );
  const persistRequestRef = useRef(false);
  const commitTargetPathRef = useRef('');

  useEffect(() => {
    if (persistRequestRef.current && createdImage?.['@id']) {
      setPreviewB64(null);
      setIsPersisting(false);
      setSaved(true);
      persistRequestRef.current = false;

      const targetPath = commitTargetPathRef.current;
      if (targetPath) {
        dispatch(getContent(targetPath));
        dispatch(
          searchContent(targetPath, {
            metadata_fields: '_all',
            sort_on: 'getObjPositionInParent',
          }),
        );
      }
    }
  }, [createdImage, dispatch]);

  useEffect(() => {
    if (!isGenerating) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isGenerating]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const ingestFile = useCallback(async (file?: File | null) => {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError("Le fichier déposé n'est pas une image.");
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      setError('Image trop volumineuse (max 8 Mo).');
      return;
    }
    try {
      const payload = await readFileAsBase64(file);
      setInputImage(payload);
    } catch (e: any) {
      setError(e.message || 'Lecture du fichier impossible.');
    }
  }, []);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    ingestFile(file);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };
  const onDragLeave = () => setIsDragActive(false);
  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    ingestFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const generate = async () => {
    setError(null);
    setSaved(false);
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      setError('Renseignez un prompt avant de générer.');
      return;
    }

    const body: any = {
      prompt: trimmedPrompt,
      size,
      quality,
    };
    if (inputImage) body.image = inputImage;

    setIsGenerating(true);
    try {
      const response: any = await apiRef.current.post('/@ai-image-generate', {
        data: body,
      });
      const generated = response?.image?.data;
      if (!generated) {
        setError('Réponse inattendue du service de génération.');
      } else {
        setPreviewB64(generated);
      }
    } catch (e: any) {
      const apiMsg =
        e?.response?.body?.error?.message ||
        e?.response?.body?.message ||
        e?.message ||
        'Échec de la génération.';
      setError(apiMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const commit = () => {
    if (!previewB64) return;
    setError(null);
    setSaved(false);

    const isContentsView = pathname?.endsWith('/contents');
    const targetUrl = isContentsView
      ? pathname?.replace(/\/contents$/, '') || '/'
      : content?.parent?.['@id'] ||
        (pathname ? pathname.replace(/\/(edit|add)$/, '') : '');
    if (!targetUrl) {
      setError("Impossible de déterminer le dossier pour enregistrer l'image.");
      return;
    }
    const targetPath = flattenToAppURL(targetUrl);

    setIsPersisting(true);
    persistRequestRef.current = true;
    commitTargetPathRef.current = targetPath;
    dispatch(
      createContent(
        targetPath,
        {
          '@type': 'Image',
          title: makeTitle(prompt),
          image: {
            data: previewB64,
            encoding: 'base64',
            'content-type': 'image/png',
            filename: `ai-${Date.now()}.png`,
          },
        },
        'ai-image-tool',
      ),
    );
  };

  const discard = () => {
    setPreviewB64(null);
    setError(null);
    setSaved(false);
  };

  const dropzoneClass = `ai-image-tool__dropzone${
    isDragActive ? ' ai-image-tool__dropzone--active' : ''
  }${inputImage ? ' ai-image-tool__dropzone--has-image' : ''}`;

  const previewSrc = previewB64 ? `data:image/png;base64,${previewB64}` : null;

  return (
    <div className="ai-image-tool">
      <div className="ai-image-tool__field">
        <label htmlFor="ai-prompt">Prompt</label>
        <textarea
          id="ai-prompt"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Décrivez l'image à générer…"
          disabled={isGenerating || isPersisting}
        />
      </div>

      <div className="ai-image-tool__row">
        <div className="ai-image-tool__field">
          <label htmlFor="ai-size">Taille</label>
          <select
            id="ai-size"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            disabled={isGenerating || isPersisting}
          >
            <option value="auto">Automatique</option>
            <option value="1024x1024">Carré (1024×1024)</option>
            <option value="1536x1024">Paysage (1536×1024)</option>
            <option value="1024x1536">Portrait (1024×1536)</option>
          </select>
        </div>
        <div className="ai-image-tool__field">
          <label htmlFor="ai-quality">Qualité</label>
          <select
            id="ai-quality"
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
            disabled={isGenerating || isPersisting}
          >
            <option value="auto">Auto</option>
            <option value="low">Basse</option>
            <option value="medium">Moyenne</option>
            <option value="high">Haute</option>
          </select>
        </div>
      </div>

      <div
        className={dropzoneClass}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        {inputImage ? (
          <>
            Image source chargée : <strong>{inputImage.filename}</strong>
            <br />
            <small>Cliquez ou déposez pour remplacer.</small>
          </>
        ) : (
          <>
            Glissez-déposez une image source, ou cliquez pour parcourir.
            <br />
            <small>Optionnel — laisser vide pour génération pure.</small>
          </>
        )}
      </div>
      {inputImage && (
        <button
          type="button"
          className="ai-image-tool__secondary"
          onClick={() => setInputImage(null)}
          disabled={isGenerating || isPersisting}
        >
          Retirer l'image source
        </button>
      )}

      <div className="ai-image-tool__buttons">
        <button
          type="button"
          className="ai-image-tool__primary"
          onClick={generate}
          disabled={isGenerating || isPersisting}
        >
          {isGenerating ? 'Génération…' : 'Générer'}
        </button>
        <button
          type="button"
          onClick={commit}
          disabled={!previewB64 || isPersisting || isGenerating}
        >
          {isPersisting ? 'Enregistrement…' : 'Enregistrer dans le dossier'}
        </button>
        <button
          type="button"
          onClick={discard}
          disabled={!previewB64 || isGenerating || isPersisting}
        >
          Annuler l'aperçu
        </button>
      </div>

      {error && <div className="ai-image-tool__error">{error}</div>}
      {saved && (
        <div className="ai-image-tool__success">
          Image enregistrée dans le dossier.
        </div>
      )}
      <div className="ai-image-tool__hint">
        La génération peut prendre 30 à 90 secondes en qualité haute.
      </div>

      {previewSrc && (
        <div className="ai-image-tool__preview">
          <img src={previewSrc} alt="Aperçu généré" />
        </div>
      )}
    </div>
  );
};

export default AIImageTool;
